import { setHomePlacement, appearsOnHome } from "../homePlacement";
import { pageMeta, workLabels } from "../../pages/pageMetadata";
import { convertIntroduction, convertPageDescription } from "../introService";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ZodError } from "zod";
import { assetService } from "../../assets";
import { subscriptionService, type Subscription } from "../../billing";
import { blockService } from "../../blocks";
import { canMovePage, copyNames, movedOrder, pageService, slugOrFallback } from "../../pages";
import { siteService } from "../../sites";
import { mediaUsage } from "../components/mediaUsage";
import {
  inspectLogo,
  siteDesignFrom,
  squareIconFile,
  withSiteDesign,
  type SiteDesign,
} from "../../themes";
import { AppError } from "../../../lib/appError";
import type { Json } from "../../../lib/database.types";
import type {
  BlockEditPatch,
  CreatableBlockType,
  EditorAsset,
  EditorBlock,
  EditorPage,
} from "../editor.types";

const defaultContent: Record<CreatableBlockType, Json> = {
  text: { richText: "Start writing here…" },
  page: { display: "card", showDescription: true },
  library: { layout: "grid", columns: 2 },
  picture: { alt: "", caption: "", fit: "cover" },
  quote: { text: "Add a memorable quote.", attribution: "" },
  video: { url: "", caption: "", style: "standard", playback: "player" },
  columns: { style: "even" },
  column: {},
  section: { background: "none", width: "full", spacing: "regular", align: "left" },
  button: { buttons: [{ label: "Get in touch", style: "solid" }] },
  carousel: { items: [], layout: "single", shape: "landscape", autoplay: false },
};

const sortPages = (list: EditorPage[]) =>
  [...list].sort(
    (a, b) =>
      (a.navigation_order ?? Number.MAX_SAFE_INTEGER) -
      (b.navigation_order ?? Number.MAX_SAFE_INTEGER) ||
      a.created_at.localeCompare(b.created_at),
  );

const groupByPage = (pages: EditorPage[], blocks: EditorBlock[]) => {
  const grouped: Record<string, EditorBlock[]> = Object.fromEntries(
    pages.map((page) => [page.id, []]),
  );
  for (const block of blocks) (grouped[block.page_id] ??= []).push(block);
  return grouped;
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export type PageUpdateValues = {
  description?: string | null;
  title?: string;
  slug?: string;
  is_visible?: boolean;
  seo?: Json;
};

export const useSiteEditor = (siteId: string) => {
  const [documentRevision, setDocumentRevision] = useState(0);
  const [settings, setSettings] = useState<Json>({});
  const [trash, setTrash] = useState<EditorPage[]>([]);
  const [siteName, setSiteName] = useState("");
  const [siteDescription, setSiteDescription] = useState<string | null>(null);
  const [siteSubdomain, setSiteSubdomain] = useState<string | null>(null);
  const [siteVersionId, setSiteVersionId] = useState("");
  const [pages, setPages] = useState<EditorPage[]>([]);
  const [currentPageId, setCurrentPageId] = useState("");
  // Every page's blocks, loaded together so moving between pages is instant.
  const [documents, setDocuments] = useState<Record<string, EditorBlock[]>>({});
  const [assets, setAssets] = useState<EditorAsset[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(true);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [creatingBlock, setCreatingBlock] = useState<CreatableBlockType | null>(
    null,
  );
  const [actionError, setActionError] = useState("");
  const [error, setError] = useState("");
  // Set when the loaded ids can no longer be saved to: the site was published
  // from elsewhere, or a publish here succeeded but reloading it did not.
  const [staleReason, setStaleReason] = useState<
    "published" | "elsewhere" | null
  >(null);
  const siteVersionIdRef = useRef("");
  const currentSlugRef = useRef<string | undefined>(undefined);
  const publishEpoch = useRef(0);

  const loadSite = useCallback(async () => {
    let { site, draft, published } = await siteService.getEditorSite(siteId);
    const [loadedPages, initialBlocks, loadedAssets, billing, loadedTrash] =
      await Promise.all([
        pageService.list(draft.id),
        blockService.listForVersion(draft.id),
        assetService.list(siteId),
        subscriptionService.load(),
        pageService.listTrash(draft.id),
      ]);
    let loadedBlocks = initialBlocks;
    const home = loadedPages.find((page) => page.is_home);
    if (home && draft.description) {
      await convertIntroduction(draft.id, home, draft.description);
      ({ site, draft, published } = await siteService.getEditorSite(siteId));
      loadedBlocks = await blockService.listForVersion(draft.id);
    }
    const descriptions = loadedPages.filter(
      (p) =>
        !p.is_home && p.description && pageMeta(p).descriptionInBlocks !== true,
    );
    if (descriptions.length) {
      for (const p of descriptions) await convertPageDescription(p);
      loadedBlocks = await blockService.listForVersion(draft.id);
    }
    return {
      site,
      draft,
      published,
      loadedPages: descriptions.length
        ? await pageService.list(draft.id)
        : loadedPages,
      loadedBlocks,
      loadedAssets,
      billing,
      loadedTrash,
    };
  }, [siteId]);

  const applySite = (
    loaded: Awaited<ReturnType<typeof loadSite>>,
    keepSlug?: string,
  ) => {
    const firstPage =
      loaded.loadedPages.find((page) => page.is_home) ?? loaded.loadedPages[0];
    const sameSlug = keepSlug
      ? loaded.loadedPages.find((page) => page.slug === keepSlug)
      : undefined;
    siteVersionIdRef.current = loaded.draft.id;
    setSettings(loaded.draft.settings);
    setTrash(loaded.loadedTrash);
    setSiteName(loaded.draft.name);
    setSiteDescription(loaded.draft.description);
    setSiteSubdomain(loaded.site.subdomain);
    setSiteVersionId(loaded.draft.id);
    setPages(sortPages(loaded.loadedPages));
    setDocuments(groupByPage(loaded.loadedPages, loaded.loadedBlocks));
    setAssets(loaded.loadedAssets);
    // Keep the page the user is on when it survives the reload (e.g. after a
    // Ducky turn); after a publish every id is new, so find it again by slug.
    // Fall back to the homepage on first load, a site switch, or when the page
    // is gone.
    setCurrentPageId((current) =>
      loaded.loadedPages.some((page) => page.id === current)
        ? current
        : (sameSlug?.id ?? firstPage?.id ?? ""),
    );
    setSubscription(loaded.billing.subscription);
    setIsSubscribed(loaded.billing.isActive);
    setIsPublished(loaded.site.published_version_id !== null);
    // The fresh draft created by publish_site shares the publish transaction's
    // timestamp, so draft.updated_at > published.published_at means the draft
    // has diverged from what is live.
    const publishedAt = loaded.published?.published_at;
    setHasUnpublishedChanges(
      !publishedAt ||
      Date.parse(loaded.draft.updated_at) > Date.parse(publishedAt),
    );
  };

  useEffect(() => {
    let active = true;
    void loadSite()
      .then((loaded) => {
        if (active) applySite(loaded);
      })
      .catch((loadError: unknown) => {
        if (active)
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Could not load this website.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadSite]);

  const cached = currentPageId in documents;
  useEffect(() => {
    if (!currentPageId || cached) return;
    let active = true;
    void blockService
      .list(currentPageId)
      .then((loadedBlocks) => {
        if (active)
          setDocuments((current) => ({
            ...current,
            [currentPageId]: loadedBlocks,
          }));
      })
      .catch((loadError: unknown) => {
        if (active)
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Could not load this page.",
          );
      });
    return () => {
      active = false;
    };
  }, [currentPageId, cached]);
  const blocks = useMemo(
    () => documents[currentPageId] ?? [],
    [documents, currentPageId],
  );
  const blocksLoading = !cached;
  const setBlocks = (
    next: EditorBlock[] | ((current: EditorBlock[]) => EditorBlock[]),
  ) =>
    setDocuments((current) => ({
      ...current,
      [currentPageId]:
        typeof next === "function" ? next(current[currentPageId] ?? []) : next,
    }));

  const currentPage = pages.find((page) => page.id === currentPageId);
  useEffect(() => {
    currentSlugRef.current = currentPage?.slug;
  }, [currentPage?.slug]);
  const rootBlocks = blocks.filter(
    (block) => block.parent_source_block_id === null,
  );
  const assetsById = useMemo(
    () => new Map(assets.map((asset) => [asset.id, asset])),
    [assets],
  );

  const selectPage = (pageId: string) => {
    if (pageId === currentPageId) return;
    // An id from before a publish no longer exists; land on Home, not a blank page.
    const target =
      pages.length && !pages.some((page) => page.id === pageId)
        ? (pages.find((page) => page.is_home) ?? pages[0])
        : undefined;
    setCurrentPageId(target?.id ?? pageId);
  };

  const deleteBlock = async (blockId: string) => {
    setActionError("");
    try {
      await blockService.remove(blockId);
      setBlocks((current) => current.filter((block) => block.id !== blockId));
      setHasUnpublishedChanges(true);
      return true;
    } catch (deleteError) {
      setActionError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete the block.",
      );
      return false;
    }
  };

  const createBlock = async (
    blockType: CreatableBlockType,
    file?: File,
    parentSourceBlockId?: string | null,
  ) => {
    if (!currentPage) return false;
    const targetPage = pages.find((page) => page.id !== currentPage.id);
    if (blockType === "page" && !targetPage) {
      setActionError("Create another page before adding a Page block.");
      return false;
    }

    setCreatingBlock(blockType);
    setActionError("");
    let uploadedAsset: EditorAsset | undefined;
    try {
      if (blockType === "picture") {
        if (!file) return false;
        const uploaded = await assetService.upload(siteId, file);
        uploadedAsset = uploaded.asset;
        setAssets((current) => [...current, uploaded.asset]);
      }

      const siblings = parentSourceBlockId
        ? blocks.filter(
          (block) => block.parent_source_block_id === parentSourceBlockId,
        )
        : rootBlocks;

      const created = await blockService.create({
        site_version_id: currentPage.site_version_id,
        page_id: currentPage.id,
        block_type: blockType,
        parent_source_block_id: parentSourceBlockId ?? null,
        position: Math.max(-1, ...siblings.map((block) => block.position)) + 1,
        content: defaultContent[blockType],
        target_page_source_id:
          blockType === "page" ? targetPage?.source_page_id : null,
        asset_id: uploadedAsset?.id ?? null,
      });
      setBlocks((current) => [...current, created]);
      setHasUnpublishedChanges(true);
      return true;
    } catch (createError) {
      setActionError(
        createError instanceof Error
          ? createError.message
          : "Could not create the block.",
      );
      return false;
    } finally {
      setCreatingBlock(null);
    }
  };

  const updateBlock = async (
    block: EditorBlock,
    patch: BlockEditPatch,
  ): Promise<string | null> => {
    try {
      const updated = await blockService.update(block.id, block.block_type, {
        content: patch.content,
        target_page_source_id: patch.target_page_source_id,
      });
      setBlocks((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setHasUnpublishedChanges(true);
      return null;
    } catch (updateError) {
      if (updateError instanceof ZodError)
        return "Some of the supplied data is invalid.";
      return updateError instanceof Error
        ? updateError.message
        : "Could not save the block.";
    }
  };

  const reorderBlocks = async (orderedSourceBlockIds: string[]) => {
    if (!currentPage) return false;
    const previous = blocks;
    const positionBySourceId = new Map(
      orderedSourceBlockIds.map((sourceBlockId, index) => [
        sourceBlockId,
        index,
      ]),
    );
    setBlocks((current) => [
      ...current
        .filter((block) => block.parent_source_block_id === null)
        .map((block) => ({
          ...block,
          position:
            positionBySourceId.get(block.source_block_id) ?? block.position,
        }))
        .sort((a, b) => a.position - b.position),
      ...current.filter((block) => block.parent_source_block_id !== null),
    ]);
    try {
      await blockService.reorder(currentPage.id, null, orderedSourceBlockIds);
      setHasUnpublishedChanges(true);
      return true;
    } catch (reorderError) {
      setBlocks(previous);
      setActionError(
        reorderError instanceof Error
          ? reorderError.message
          : "Could not reorder the blocks.",
      );
      return false;
    }
  };

  const createPage = async (
    title: string,
    work = false,
  ): Promise<string | null> => {
    if (!siteVersionId) return "Pages are still loading.";
    try {
      const created = await pageService.create({
        site_version_id: siteVersionId,
        title,
        slug: `${slugOrFallback(title)}-${crypto.randomUUID().slice(0, 6)}`,
        is_visible: !work,
        page_type: work ? "project" : "standard",
        seo: { work },
        navigation_order:
          Math.max(-1, ...pages.map((page) => page.navigation_order ?? -1)) + 1,
      });
      setPages((current) => sortPages([...current, created]));
      setDocuments((current) => ({ ...current, [created.id]: [] }));
      selectPage(created.id);
      setHasUnpublishedChanges(true);
      return null;
    } catch (createError) {
      return errorMessage(createError, "Could not create the page.");
    }
  };

  const updatePage = async (
    pageId: string,
    values: PageUpdateValues,
  ): Promise<string | null> => {
    try {
      // A slug with nothing Latin in it falls back to "page", "page-2"…
      const slug =
        values.slug === undefined
          ? undefined
          : slugOrFallback(
              values.slug,
              [...pages, ...trash]
                .filter((page) => page.id !== pageId)
                .map((page) => page.slug),
            );
      const updated = await pageService.update(pageId, { ...values, slug });
      setPages((current) =>
        sortPages(
          current.map((page) => (page.id === updated.id ? updated : page)),
        ),
      );
      setHasUnpublishedChanges(true);
      return null;
    } catch (updateError) {
      return errorMessage(updateError, "Could not save the page.");
    }
  };

  const updateSiteDetails = async (values: {
    name?: string;
    description?: string | null;
  }): Promise<string | null> => {
    if (!siteVersionId) return "Site is still loading.";
    try {
      const updated = await siteService.updateDraft(siteVersionId, values);
      if (values.name !== undefined) setSiteName(updated.name);
      if (values.description !== undefined)
        setSiteDescription(updated.description);
      setHasUnpublishedChanges(true);
      return null;
    } catch (error) {
      return errorMessage(error, "Could not save the site details.");
    }
  };

  const updateDesign = async (
    patch: Partial<SiteDesign>,
  ): Promise<string | null> => {
    if (!siteVersionId) return "Site is still loading.";
    const previous = settings;
    setSettings(withSiteDesign(previous, patch));
    try {
      const updated = await siteService.updateDesign(siteVersionId, patch);
      setSettings(updated.settings);
      setHasUnpublishedChanges(true);
      return null;
    } catch (error) {
      setSettings(previous);
      return errorMessage(error, "Could not save the design.");
    }
  };

  // Replaced icons are kept: the published version may still show them.
  const uploadSiteIcon = async (file: File): Promise<string | null> => {
    try {
      const { asset } = await assetService.upload(
        siteId,
        await squareIconFile(file),
        "Site icon",
      );
      setAssets((current) => [...current, asset]);
      return updateDesign({ icon: asset.id });
    } catch (error) {
      return errorMessage(error, "Could not upload the icon.");
    }
  };

  /** Uploads an image for the site (a work's cover, say) and keeps it to hand. */
  const uploadImage = async (file: File): Promise<EditorAsset> => {
    const { asset } = await assetService.upload(siteId, file);
    setAssets((current) => [...current, asset]);
    return asset;
  };

  /** Saves an image's description (its alt text) for every place it's used. */
  const updateAssetAlt = async (asset: EditorAsset, alt: string): Promise<string | null> => {
    try {
      const updated = await assetService.updateAltText(asset.id, alt);
      setAssets((current) => current.map((item) => (item.id === asset.id ? { ...item, ...updated } : item)));
      return null;
    } catch (error) {
      return errorMessage(error, "Could not save the description.");
    }
  };

  /** Puts an image in a folder of the Media library (null: no folder). */
  const moveAssetToFolder = async (asset: EditorAsset, folder: string | null): Promise<string | null> => {
    try {
      const updated = await assetService.updateFolder(asset.id, folder);
      setAssets((current) => current.map((item) => (item.id === asset.id ? { ...item, ...updated } : item)));
      return null;
    } catch (error) {
      if (/folder/i.test(String((error as { cause?: { message?: string } })?.cause?.message ?? error)))
        return "Folders need a site update that isn’t live yet. Try again soon.";
      return errorMessage(error, "Could not move the image.");
    }
  };

  /**
   * Deletes an image for good, but only once nothing uses it: not this
   * draft, and not the live site (which keeps showing what was published).
   */
  const deleteAsset = async (asset: EditorAsset): Promise<string | null> => {
    try {
      const inDraft = mediaUsage(asset.id, { documents, pages, design: siteDesignFrom(settings) });
      if (inDraft.length) return `It’s still used on ${inDraft.join(", ")}. Remove it there first.`;
      const { published } = await siteService.getEditorSite(siteId);
      if (published) {
        const [liveBlocks, livePages] = await Promise.all([
          blockService.listForVersion(published.id),
          pageService.list(published.id),
        ]);
        const byPage: Record<string, EditorBlock[]> = {};
        for (const block of liveBlocks) (byPage[block.page_id] ??= []).push(block);
        const live = mediaUsage(asset.id, { documents: byPage, pages: livePages, design: siteDesignFrom(published.settings) });
        if (live.length) return `Your live site still shows it (${live.join(", ")}). Publish without it first.`;
      }
      await assetService.remove(asset.id);
      setAssets((current) => current.filter((item) => item.id !== asset.id));
      return null;
    } catch (error) {
      return errorMessage(error, "Could not delete the image.");
    }
  };

  // Like icons, replaced logos are kept for the published version.
  const uploadSiteLogo = async (file: File): Promise<string | null> => {
    try {
      const [{ asset }, { tone, info }] = await Promise.all([
        assetService.upload(siteId, file, "Logo"),
        inspectLogo(file),
      ]);
      setAssets((current) => [...current, asset]);
      return updateDesign({ logo: asset.id, logoTone: tone, logoInfo: info });
    } catch (error) {
      return errorMessage(error, "Could not upload the logo.");
    }
  };

  const deletePage = async (pageId: string): Promise<string | null> => {
    if (pages.length <= 1) return "Your site needs at least one page.";
    try {
      const target = pages.find((p) => p.id === pageId);
      if (!target) return "Page not found.";
      if (target.is_home) return "Choose another homepage first.";
      const removed = await pageService.trash(target);
      setTrash((current) => [...current, removed]);
      const remaining = pages.filter((page) => page.id !== pageId);
      setPages((current) => current.filter((p) => p.id !== pageId));
      if (pageId === currentPageId) {
        selectPage((remaining.find((page) => page.is_home) ?? remaining[0]).id);
      }
      setHasUnpublishedChanges(true);
      return null;
    } catch (deleteError) {
      return errorMessage(deleteError, "Could not delete the page.");
    }
  };

  const restorePage = async (page: EditorPage): Promise<string | null> => {
    try {
      const restored = await pageService.restore(page);
      setPages((current) => sortPages([...current, restored]));
      setTrash((current) => current.filter((p) => p.id !== page.id));
      setHasUnpublishedChanges(true);
      return null;
    } catch (error) {
      return errorMessage(error, "Could not restore the page.");
    }
  };
  const removeSamples = async (): Promise<string | null> => {
    for (const p of pages.filter((p) => pageMeta(p).sample === true)) {
      const error = await deletePage(p.id);
      if (error) return error;
    }
    return null;
  };
  const duplicatePage = async (page: EditorPage): Promise<string | null> => {
    if (!siteVersionId) return "Pages are still loading.";
    try {
      const names = copyNames(
        page,
        pages.map((p) => p.title),
        [...pages, ...trash].map((p) => p.slug),
      );
      await pageService.duplicate(page.id, names.title, names.slug);
      setPages(sortPages(await pageService.list(siteVersionId)));
      setHasUnpublishedChanges(true);
      return null;
    } catch (duplicateError) {
      return errorMessage(duplicateError, "Could not duplicate the page.");
    }
  };

  const movePage = async (
    pageId: string,
    delta: -1 | 1,
  ): Promise<string | null> => {
    if (!siteVersionId) return "Pages are still loading.";
    // Swap with the neighbour of the same kind, so the move shows in its list.
    const orderedIds = movedOrder(pages, pageId, delta);
    if (!orderedIds) return null;
    const previous = pages;
    const byId = new Map(pages.map((page) => [page.id, page]));
    setPages(
      orderedIds.map((id, i) => ({ ...byId.get(id)!, navigation_order: i })),
    );
    try {
      await pageService.reorder(siteVersionId, orderedIds);
      setHasUnpublishedChanges(true);
      return null;
    } catch (reorderError) {
      setPages(previous);
      return errorMessage(reorderError, "Could not reorder the pages.");
    }
  };

  const setHomePage = async (pageId: string): Promise<string | null> => {
    try {
      await pageService.setHome(pageId);
      setPages((current) =>
        current.map((page) => ({ ...page, is_home: page.id === pageId })),
      );
      setHasUnpublishedChanges(true);
      return null;
    } catch (homeError) {
      return errorMessage(homeError, "Could not set the homepage.");
    }
  };

  const updateSubdomain = async (
    value: string | null,
  ): Promise<string | null> => {
    try {
      const updated = await siteService.updateSubdomain(siteId, value);
      setSiteSubdomain(updated.subdomain);
      return null;
    } catch (updateError) {
      return errorMessage(updateError, "Could not save the site address.");
    }
  };

  const refreshSubscription = useCallback(async () => {
    try {
      const { subscription: row, isActive } = await subscriptionService.load();
      setSubscription(row);
      setIsSubscribed(isActive);
    } catch (refreshError) {
      setActionError(
        errorMessage(refreshError, "Could not refresh your subscription."),
      );
    }
  }, []);

  const publishSite = async (): Promise<boolean> => {
    setPublishing(true);
    setActionError("");
    publishEpoch.current += 1;
    // Publishing swaps the draft, so every version/page/block id changes;
    // remember the page by slug to find it again.
    const slug = currentPage?.slug;
    try {
      await siteService.publish(siteId);
    } catch (publishError) {
      setPublishing(false);
      publishEpoch.current += 1;
      if (
        publishError instanceof AppError &&
        publishError.code === "SUBSCRIPTION_REQUIRED"
      ) {
        throw publishError;
      }
      setActionError(
        errorMessage(publishError, "Could not publish the website."),
      );
      return false;
    }
    // It is live from here on, even if reloading the new draft fails.
    setIsPublished(true);
    setHasUnpublishedChanges(false);
    try {
      applySite(await loadSite(), slug);
      setStaleReason(null);
    } catch {
      setStaleReason("published");
    } finally {
      setPublishing(false);
      publishEpoch.current += 1;
    }
    return true;
  };

  // Reload after an external change (publish, agent turn). The hook
  // recomputes the same `loadSite`/`applySite` pair the initial mount and
  // publish flow already use; expose it so the agent panel can request a
  // refresh after applied tool rows.
  const reloadSite = useCallback(async () => {
    applySite(await loadSite(), currentSlugRef.current);
    setStaleReason(null);
    // The current page now survives a reload, so remount its document editor
    // to show blocks the agent changed.
    setDocumentRevision((n) => n + 1);
  }, [loadSite]);

  // Another device may have published since this one loaded; its draft ids
  // are gone, so saving here would fail. Check when the tab comes back.
  useEffect(() => {
    const check = async () => {
      if (document.visibilityState === "hidden" || !siteVersionIdRef.current)
        return;
      const epoch = publishEpoch.current;
      try {
        const latest = await siteService.draftVersionId(siteId);
        if (
          latest &&
          epoch === publishEpoch.current &&
          latest !== siteVersionIdRef.current
        )
          setStaleReason((current) => current ?? "elsewhere");
      } catch {
        // Offline or signed out: nothing to report from a background check.
      }
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [siteId]);

  return {
    staleReason,
    canMovePage: (pageId: string, delta: -1 | 1) =>
      canMovePage(pages, pageId, delta),
    documentRevision,
    showOnHome: (page: EditorPage) =>
      appearsOnHome(
        page,
        pages.find((p) => p.is_home),
        documents,
      ),
    setShowOnHome: async (
      page: EditorPage,
      show: boolean,
    ): Promise<string | null> => {
      const home = pages.find((p) => p.is_home);
      if (!home) return "Homepage not found.";
      try {
        const blocks = await setHomePlacement(home, page, pages, show);
        setDocuments((current) => ({ ...current, [home.id]: blocks }));
        setDocumentRevision((n) => n + 1);
        setHasUnpublishedChanges(true);
        return null;
      } catch (error) {
        return errorMessage(error, "Could not update Home.");
      }
    },
    labels: workLabels(settings),
    design: siteDesignFrom(settings),
    updateDesign,
    uploadSiteIcon,
    uploadSiteLogo,
    uploadImage,
    updateAssetAlt,
    moveAssetToFolder,
    deleteAsset,
    trash,
    restorePage,
    removeSamples,
    siteName,
    siteDescription,
    siteSubdomain,
    pages,
    currentPage,
    currentPageId,
    blocks,
    rootBlocks,
    assetsById,
    loading,
    blocksLoading,
    creatingBlock,
    publishing,
    subscription,
    isSubscribed,
    isPublished,
    hasUnpublishedChanges,
    actionError,
    error,
    reloadSite,
    assetCreated: (asset: EditorAsset) => setAssets(current => [...current, asset]),
    documents,
    pageCreated: (page: EditorPage) => {
      setPages((current) => sortPages([...current, page]));
      setDocuments((current) => ({
        ...current,
        [page.id]: current[page.id] ?? [],
      }));
    },
    documentSaved: (savedBlocks: EditorBlock[], pageId: string) => {
      setDocuments((current) => ({ ...current, [pageId]: savedBlocks }));
      setHasUnpublishedChanges(true);
    },
    clearActionError: () => setActionError(""),
    showActionError: setActionError,
    selectPage,
    createBlock,
    updateBlock,
    deleteBlock,
    reorderBlocks,
    createPage,
    updatePage,
    deletePage,
    duplicatePage,
    movePage,
    setHomePage,
    publishSite,
    refreshSubscription,
    updateSubdomain,
    updateSiteDetails,
  };
};
